import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertCircle,
  Loader2,
  ArrowLeft,
  Check,
  Mail,
  Lock,
} from "lucide-react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { useApolloClient } from "@apollo/client/react";
import {
  AUTHENTICATE_WITH_GOOGLE,
  SIGN_IN_WITH_EMAIL,
  SEND_VERIFICATION_CODE,
  RESET_PASSWORD_WITH_CODE,
} from "@/graphql/mutations";
import { GET_CURRENT_USER } from "@/graphql/queries";
import { setAuthCookie, removeAuthCookie } from "@/lib/cookie-utils";
import { isImpersonating } from "@/lib/session-utils";
import { clearTokens, getAccessToken, saveTokens } from "@/lib/token-utils";
import { trackDashboardEvent } from "@/lib/analytics";

// Google Identity Services types
interface GoogleConfig {
  client_id: string;
  callback: (response: { credential: string }) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  itp_support?: boolean;
}

interface GoogleButtonConfig {
  theme?: string;
  size?: string;
  width?: string;
  text?: string;
  locale?: string;
  type?: string;
}

interface GoogleIdentity {
  accounts: {
    id: {
      initialize: (config: GoogleConfig) => void;
      renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void;
      prompt: (
        notification?: (notification: {
          isNotDisplayed: () => boolean;
          isSkippedMoment: () => boolean;
        }) => void,
      ) => void;
    };
  };
}

declare const google: GoogleIdentity | undefined;

interface AuthMutationResult {
  success: boolean;
  error?: string | null;
  user?: {
    avatarURL?: string;
    onboardingCompleted?: boolean;
    [key: string]: unknown;
  } | null;
  tokens?: { accessToken: string; refreshToken: string } | null;
  organizations?: { id: string; name?: string }[] | null;
  requiresOnboarding?: boolean | null;
}

const LOOPBACK_BROADCASTER_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "::1",
  "[::1]",
]);

const getSafeBroadcasterCallback = (
  rawCallback: string | null,
): string | null => {
  if (!rawCallback) {
    return null;
  }

  try {
    const callbackUrl = new URL(rawCallback);
    const hasAllowedProtocol =
      callbackUrl.protocol === "http:" || callbackUrl.protocol === "https:";
    const hasAllowedHost = LOOPBACK_BROADCASTER_HOSTS.has(callbackUrl.hostname);
    const hasCredentials =
      callbackUrl.username !== "" || callbackUrl.password !== "";

    if (!hasAllowedProtocol || !hasAllowedHost || hasCredentials) {
      return null;
    }

    return callbackUrl.toString();
  } catch {
    return null;
  }
};

// Chrome Translate can wrap button labels with extra DOM nodes, which breaks
// React's text-node removals during auth CTA state changes and unmounts.
const AuthButtonLabel = ({ children }: { children: ReactNode }) => (
  <span className="notranslate" translate="no">
    {children}
  </span>
);

const Login = () => {
  const [error, setError] = useState("");
  const [isInitializing, setIsInitializing] = useState(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [broadcasterCallbackSent, setBroadcasterCallbackSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState<"email" | "verify">("email");
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const buttonRef = useRef<HTMLDivElement>(null);
  const apolloClient = useApolloClient();

  useEffect(() => {
    trackDashboardEvent("auth_view", {
      auth_flow: "login",
      page_path: "/login",
      has_invitation: Boolean(searchParams.get("invitation")),
      from_broadcaster: Boolean(searchParams.get("broadcaster_callback")),
    });
  }, [searchParams]);

  // Check for broadcaster callback URL
  const rawBroadcasterCallback = searchParams.get("broadcaster_callback");
  const broadcasterCallback = getSafeBroadcasterCallback(
    rawBroadcasterCallback,
  );
  const isFromBroadcaster = !!broadcasterCallback;

  // Check for invitation token (from invitation email link)
  const invitationToken = searchParams.get("invitation");

  // Check for logout request (from broadcaster app)
  const shouldLogout = searchParams.get("logout") === "true";

  // Google OAuth Client ID - Frontend client ID
  const GOOGLE_CLIENT_ID =
    import.meta.env.VITE_GOOGLE_CLIENT_ID ||
    "787453514477-33m2jvargdpl27nkg8na1e4dmnq67f82.apps.googleusercontent.com";

  // Check if user is already authenticated
  const authToken = getAccessToken();

  // Handle logout request (must be after authToken declaration)
  useEffect(() => {
    if (shouldLogout && authToken) {
      clearTokens();
      if (!isImpersonating()) {
        removeAuthCookie();
      }
      // Remove the logout param from URL
      window.history.replaceState({}, "", "/login");
      window.location.reload();
    }
  }, [shouldLogout, authToken]);

  useEffect(() => {
    if (rawBroadcasterCallback && !broadcasterCallback) {
      setError("Invalid broadcaster callback URL");
    }
  }, [rawBroadcasterCallback, broadcasterCallback]);

  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useQuery<{
    me?: { email?: string; organizations?: { id: string }[] } | null;
  }>(GET_CURRENT_USER, {
    skip: !authToken, // Only run query if authToken exists
    errorPolicy: "all", // Don't throw error if query fails (token might be invalid)
  });

  // Send token to broadcaster app
  const sendTokenToBroadcaster = useCallback(
    async (token: string, refreshToken?: string) => {
      if (!broadcasterCallback || broadcasterCallbackSent) return false;

      try {
        const callbackUrl = new URL(broadcasterCallback);
        callbackUrl.searchParams.set("token", token);
        if (refreshToken) {
          callbackUrl.searchParams.set("refresh_token", refreshToken);
        }
        window.location.href = callbackUrl.toString();
        setBroadcasterCallbackSent(true);
        return true;
      } catch (err) {
        console.error("Failed to send token to broadcaster:", err);
        return false;
      }
    },
    [broadcasterCallback, broadcasterCallbackSent],
  );

  // Handle authentication check result
  useEffect(() => {
    if (!authToken) {
      // No auth token, skip checking
      setIsCheckingAuth(false);
      return;
    }

    if (userLoading) {
      // Still loading, keep checking
      return;
    }

    if (userError) {
      clearTokens();
      if (!isImpersonating()) {
        removeAuthCookie();
      }
      setIsCheckingAuth(false);
      return;
    }

    const user = userData?.me;
    if (user) {
      if (user.organizations && user.organizations.length > 0) {
        // User is authenticated and has an organization
        if (isFromBroadcaster) {
          // Coming from broadcaster - just show the "Return to Broadcaster" screen
          // Don't auto-redirect, let user click the button
          setIsCheckingAuth(false);
        } else {
          // Normal flow - redirect to dashboard
          navigate("/");
        }
      } else {
        // User is authenticated but has no organization (needs onboarding)
        setIsCheckingAuth(false);
      }
    } else {
      // No user data, show login page
      setIsCheckingAuth(false);
    }
  }, [
    userData,
    userLoading,
    userError,
    authToken,
    navigate,
    isFromBroadcaster,
    sendTokenToBroadcaster,
  ]);

  const [authenticateWithGoogle, { loading: isLoading }] = useMutation<{
    authenticateWithGoogle: AuthMutationResult;
  }>(AUTHENTICATE_WITH_GOOGLE, {
    onCompleted: async (data) => {
      console.log("GraphQL response received:", data);

      if (!data || !data.authenticateWithGoogle) {
        console.error("Invalid response structure:", data);
        setError("Invalid response from server. Please try again.");
        return;
      }

      const result = data.authenticateWithGoogle;

      if (!result || result.success === undefined) {
        console.error("Invalid result structure:", result);
        setError("Invalid authentication response. Please try again.");
        return;
      }

      if (!result.success) {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_google",
          result: "failure",
          error_message: result.error || "authentication_failed",
        });
        setError(result.error || "Authentication failed. Please try again.");
        return;
      }

      // Ensure user exists when success is true
      if (!result.user) {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_google",
          result: "failure",
          error_message: "missing_user",
        });
        setError(
          "Authentication succeeded but user data is missing. Please try again.",
        );
        return;
      }

      // Login starts a different auth flow; do not reuse an abandoned signup answer.
      sessionStorage.removeItem("pendingSignupSource");

      // Get Google ID token from sessionStorage (stored before mutation call)
      const googleIdToken = sessionStorage.getItem("pendingGoogleIdToken");

      // Store authentication tokens
      if (result.tokens) {
        console.log("Storing authentication tokens...");
        saveTokens(result.tokens.accessToken, result.tokens.refreshToken);
        setAuthCookie(result.tokens.accessToken);
        console.log("Tokens stored, clearing Apollo cache...");
        // Stop in-flight queries before clearing to avoid Invariant Violation #89
        apolloClient.stop();
        // Clear Apollo cache to ensure fresh queries with new token
        await apolloClient.clearStore();
        console.log("Apollo cache cleared");

        // Write user data (including avatarURL) to cache after authentication
        if (result.user) {
          try {
            apolloClient.cache.writeQuery({
              query: GET_CURRENT_USER,
              data: {
                me: {
                  ...result.user,
                  // Ensure organizations are included if available
                  organizations: result.organizations || [],
                },
              },
            });
            console.log(
              "User data written to cache, including avatarURL:",
              result.user.avatarURL,
            );
          } catch (error) {
            console.warn("Failed to write user data to cache:", error);
            // Non-critical error, continue with navigation
          }
        }

        // If coming from broadcaster, send token back
        if (isFromBroadcaster) {
          const hasOrganizations =
            result.organizations && result.organizations.length > 0;
          if (hasOrganizations) {
            sendTokenToBroadcaster(
              result.tokens.accessToken,
              result.tokens.refreshToken,
            );
            return;
          }
        }
      } else {
        // No tokens - user needs onboarding, store Google ID token temporarily
        if (googleIdToken) {
          sessionStorage.setItem("pendingGoogleIdToken", googleIdToken);
        }
        // Redirect to onboarding if no tokens
        navigate("/onboarding");
        return;
      }

      // Check if onboarding is required
      // Only check requiresOnboarding flag if tokens exist (user has organizations)
      // If tokens exist but requiresOnboarding is true, it means onboarding wasn't completed properly
      const hasOrganizations =
        result.organizations && result.organizations.length > 0;
      const onboardingCompleted = result.user?.onboardingCompleted === true;
      const requiresOnboarding = result.requiresOnboarding === true;

      console.log("Onboarding check:", {
        hasOrganizations,
        onboardingCompleted,
        requiresOnboarding,
        organizationsCount: result.organizations?.length || 0,
        user: result.user,
      });

      const needsOnboarding =
        requiresOnboarding || !onboardingCompleted || !hasOrganizations;

      if (needsOnboarding) {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_google",
          result: "success",
          destination: "onboarding",
        });
        // User needs to complete onboarding
        console.log("User needs onboarding, redirecting to onboarding page");
        navigate("/onboarding");
        return;
      }

      // Onboarding is complete - check organizations
      console.log(
        "Onboarding complete, checking organizations:",
        result.organizations,
      );
      if (result.organizations && result.organizations.length > 1) {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_google",
          result: "success",
          destination: "dashboard",
          organization_count: result.organizations.length,
        });
        // Multiple organizations - let user choose
        // User has organization, redirect to dashboard (user belongs to one tenant)
        console.log("Organization found, redirecting to dashboard");
        setTimeout(() => {
          window.location.href = "/";
        }, 100);
      } else if (result.organizations && result.organizations.length === 1) {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_google",
          result: "success",
          destination: "dashboard",
          organization_count: 1,
        });
        // Single organization - set it and go to dashboard
        console.log("Single organization found, redirecting to dashboard");
        // Small delay to ensure tokens are fully stored and cache is cleared
        setTimeout(() => {
          // Use window.location.href to force full page reload and ensure token is used
          window.location.href = "/";
        }, 100);
      } else {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_google",
          result: "success",
          destination: "onboarding",
          organization_count: 0,
        });
        // Should not happen if onboarding is complete, but redirect to onboarding just in case
        console.warn(
          "Unexpected state: tokens exist but no organizations, redirecting to onboarding",
        );
        navigate("/onboarding");
      }
    },
    onError: (err) => {
      const isNetworkError =
        !!(err as { networkError?: unknown }).networkError ||
        err.message?.includes("Failed to fetch") ||
        err.message?.includes("NetworkError");

      const errorMessage = isNetworkError
        ? "Something went wrong. Please check your connection and try again."
        : "Failed to sign in with Google. Please try again.";

      setError(errorMessage);
      trackDashboardEvent("auth_result", {
        auth_flow: "login_google",
        result: "failure",
        error_message: isNetworkError ? "network_error" : errorMessage,
      });
    },
  });

  // Email sign-in mutation
  const [signInWithEmail, { loading: emailLoading }] = useMutation<{
    signInWithEmail: AuthMutationResult;
  }>(SIGN_IN_WITH_EMAIL, {
    onCompleted: async (data) => {
      const result = data.signInWithEmail;

      if (!result.success) {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_email",
          result: "failure",
          error_message: result.error || "invalid_credentials",
        });
        setError(result.error || "Invalid email or password");
        return;
      }

      sessionStorage.removeItem("pendingSignupSource");

      if (result.tokens) {
        saveTokens(result.tokens.accessToken, result.tokens.refreshToken);
        setAuthCookie(result.tokens.accessToken);
        apolloClient.stop();
        await apolloClient.clearStore();

        if (result.user) {
          apolloClient.cache.writeQuery({
            query: GET_CURRENT_USER,
            data: {
              me: {
                ...result.user,
                organizations: result.organizations || [],
              },
            },
          });
        }
      }

      if (result.requiresOnboarding) {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_email",
          result: "success",
          destination: "onboarding",
        });
        sessionStorage.setItem("pendingEmailSignup", email);
        navigate("/onboarding");
        return;
      }

      if (result.organizations && result.organizations.length > 0) {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_email",
          result: "success",
          destination: "dashboard",
          organization_count: result.organizations.length,
        });
        setTimeout(() => {
          window.location.href = "/";
        }, 100);
      } else {
        trackDashboardEvent("auth_result", {
          auth_flow: "login_email",
          result: "success",
          destination: "onboarding",
          organization_count: 0,
        });
        navigate("/onboarding");
      }
    },
    onError: (err) => {
      const isNetworkError =
        !!(err as { networkError?: unknown }).networkError ||
        err.message?.includes("Failed to fetch") ||
        err.message?.includes("NetworkError");
      const errorMessage = isNetworkError
        ? "Something went wrong. Please check your connection and try again."
        : err.message || "Failed to sign in";
      trackDashboardEvent("auth_result", {
        auth_flow: "login_email",
        result: "failure",
        error_message: isNetworkError
          ? "network_error"
          : err.message || "sign_in_failed",
      });
      setError(errorMessage);
    },
  });

  // Forgot password mutations
  const [sendVerificationCode, { loading: sendCodeLoading }] = useMutation<{
    sendVerificationCode: {
      success: boolean;
      message?: string | null;
      error?: string | null;
    };
  }>(SEND_VERIFICATION_CODE, {
    onCompleted: (data) => {
      if (data.sendVerificationCode.success) {
        setForgotStep("verify");
        setError("");
      } else {
        setError(
          data.sendVerificationCode.error || "Failed to send reset code",
        );
      }
    },
    onError: () => {
      setError("Something went wrong. Please try again.");
    },
  });

  const [resetPasswordWithCode, { loading: resetLoading }] = useMutation<{
    resetPasswordWithCode: { success: boolean; error?: string | null };
  }>(RESET_PASSWORD_WITH_CODE, {
    onCompleted: (data) => {
      if (data.resetPasswordWithCode.success) {
        setResetSuccess(true);
        setError("");
      } else {
        setError(
          data.resetPasswordWithCode.error || "Failed to reset password",
        );
      }
    },
    onError: () => {
      setError("Something went wrong. Please try again.");
    },
  });

  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      setError("Please enter your email address");
      return;
    }
    setError("");
    await sendVerificationCode({
      variables: { input: { email: forgotEmail, purpose: "PASSWORD_RESET" } },
    });
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetCode || !newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    await resetPasswordWithCode({
      variables: {
        input: { email: forgotEmail, verificationCode: resetCode, newPassword },
      },
    });
  };

  const handleBackFromForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep("email");
    setForgotEmail("");
    setResetCode("");
    setNewPassword("");
    setConfirmPassword("");
    setResetSuccess(false);
    setError("");
  };

  // Handle email sign-in
  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter email and password");
      return;
    }
    setError("");
    trackDashboardEvent("auth_submit", {
      auth_flow: "login_email",
      has_password: Boolean(password),
    });
    await signInWithEmail({
      variables: {
        input: {
          email,
          password,
        },
      },
    });
  };

  // Handle Google credential response
  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      console.log("Google credential response received");

      if (!response || !response.credential) {
        console.error("No credential received from Google:", response);
        setError("No credential received from Google. Please try again.");
        return;
      }

      console.log("Authenticating with backend...");
      setError("");
      trackDashboardEvent("auth_submit", {
        auth_flow: "login_google",
        has_invitation: Boolean(invitationToken),
      });

      try {
        // Store Google ID token in sessionStorage for onboarding if needed
        sessionStorage.setItem("pendingGoogleIdToken", response.credential);

        const result = await authenticateWithGoogle({
          variables: {
            input: {
              idToken: response.credential,
              // Include invitation token if present (from invitation email)
              ...(invitationToken && { invitationToken }),
            },
          },
        });
        console.log("Authentication mutation completed:", result);
      } catch (err) {
        console.error("Sign in error:", err);
        // Error is handled by onError callback, but log details here too
        if ((err as { graphQLErrors?: unknown }).graphQLErrors) {
          console.error(
            "GraphQL errors:",
            (err as { graphQLErrors?: unknown }).graphQLErrors,
          );
        }
        if ((err as { networkError?: unknown }).networkError) {
          console.error(
            "Network error:",
            (err as { networkError?: unknown }).networkError,
          );
        }
      }
    },
    [authenticateWithGoogle, invitationToken],
  );

  // Initialize Google Sign-In (only after auth check is complete)
  useEffect(() => {
    // Don't initialize Google Sign-In if we're still checking authentication
    // or if we're about to redirect to broadcaster
    if (isCheckingAuth || broadcasterCallbackSent) {
      return;
    }

    // If from broadcaster and already authenticated, don't show Google button
    if (isFromBroadcaster && authToken && !userError) {
      return;
    }

    let cancelled = false;
    let googleLoadInterval: ReturnType<typeof setInterval> | null = null;
    let renderRetryTimeout: ReturnType<typeof setTimeout> | null = null;
    let initialRenderTimeout: ReturnType<typeof setTimeout> | null = null;
    let styleRetryInterval: ReturnType<typeof setInterval> | null = null;
    let animationFrameId: number | null = null;

    const clearGoogleTimers = () => {
      if (googleLoadInterval) {
        clearInterval(googleLoadInterval);
      }
      if (renderRetryTimeout) {
        clearTimeout(renderRetryTimeout);
      }
      if (initialRenderTimeout) {
        clearTimeout(initialRenderTimeout);
      }
      if (styleRetryInterval) {
        clearInterval(styleRetryInterval);
      }
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };

    const initializeGoogleSignIn = () => {
      if (cancelled) {
        return;
      }

      // Must use typeof check to avoid ReferenceError when google is not defined
      if (typeof google === "undefined" || !google?.accounts?.id) {
        console.error("Google Identity Services not loaded");
        if (!cancelled) {
          setError(
            "Google Sign-In failed to load. Please check your internet connection and refresh the page.",
          );
          setIsInitializing(false);
        }
        return;
      }

      try {
        console.log(
          "Initializing Google Sign-In with client ID:",
          GOOGLE_CLIENT_ID,
        );
        console.log("Current origin:", window.location.origin);

        // Check for Google OAuth origin error
        const currentOrigin = window.location.origin;
        if (currentOrigin.includes("localhost:8080")) {
          console.warn(
            "⚠️ IMPORTANT: Make sure 'http://localhost:8080' is added to authorized JavaScript origins in Google Cloud Console",
          );
          console.warn(
            "Go to: https://console.cloud.google.com/apis/credentials",
          );
        }

        // Initialize Google Identity Services
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
        });
        console.log("Google Sign-In initialized successfully");

        // Wait a bit for the DOM to be ready, then render button
        let retryCount = 0;
        const maxRetries = 20; // 2 seconds total

        const renderButton = () => {
          if (cancelled) {
            return;
          }

          console.log(
            `Attempting to render button, retry ${retryCount + 1}/${maxRetries}, ref available:`,
            !!buttonRef.current,
          );

          if (buttonRef.current?.isConnected) {
            try {
              // Clear any existing content
              buttonRef.current.replaceChildren();

              // Calculate actual width
              const containerWidth = buttonRef.current.offsetWidth;
              const widthStr = containerWidth > 0 ? `${containerWidth}` : "400"; // Fallback if 0

              // Render Google button with larger size
              google.accounts.id.renderButton(buttonRef.current, {
                theme: "outline",
                size: "large",
                width: widthStr, // Use calculated width
                text: "signin_with",
                locale: "en",
                type: "standard",
              });

              // Apply custom styling to make button larger after it renders
              // Use MutationObserver to detect when Google button is added to DOM
              const applyButtonStyles = () => {
                if (buttonRef.current) {
                  const googleButton = buttonRef.current.querySelector(
                    'div[role="button"]',
                  ) as HTMLElement;
                  if (googleButton) {
                    googleButton.style.minHeight = "56px";
                    googleButton.style.height = "56px";
                    googleButton.style.width = "100%"; // Force full width
                    googleButton.style.fontSize = "16px";
                    googleButton.style.fontWeight = "500";
                    googleButton.style.padding = "14px 24px";
                    console.log("Google Sign-In button styled successfully");
                    return true;
                  }
                }
                return false;
              };

              // Try immediately, then retry a few times if needed
              if (!applyButtonStyles()) {
                let styleRetries = 0;
                styleRetryInterval = setInterval(() => {
                  if (cancelled) {
                    clearGoogleTimers();
                    return;
                  }

                  styleRetries++;
                  if (applyButtonStyles() || styleRetries >= 10) {
                    if (styleRetryInterval) {
                      clearInterval(styleRetryInterval);
                      styleRetryInterval = null;
                    }
                  }
                }, 100);
              }

              console.log("Google Sign-In button rendered successfully");
              if (!cancelled) {
                setIsInitializing(false);
              }
            } catch (renderErr) {
              console.error("Failed to render Google button:", renderErr);
              if (!cancelled) {
                setError(
                  `Failed to render Google Sign-In button: ${renderErr instanceof Error ? renderErr.message : "Unknown error"}. Please refresh the page.`,
                );
                setIsInitializing(false);
              }
            }
          } else {
            // Retry if ref is not ready yet
            retryCount++;
            if (retryCount < maxRetries) {
              renderRetryTimeout = setTimeout(renderButton, 100);
            } else {
              console.error(
                `Button ref not available after ${maxRetries} retries`,
              );
              if (!cancelled) {
                setError(
                  "Failed to initialize sign-in button. The page may not be fully loaded. Please refresh the page.",
                );
                setIsInitializing(false);
              }
            }
          }
        };

        // Use requestAnimationFrame to ensure DOM is ready, then small delay
        animationFrameId = requestAnimationFrame(() => {
          initialRenderTimeout = setTimeout(renderButton, 200);
        });
      } catch (err) {
        console.error("Failed to initialize Google Sign-In:", err);
        if (!cancelled) {
          setError(
            `Failed to initialize Google Sign-In: ${err instanceof Error ? err.message : "Unknown error"}. Please refresh the page.`,
          );
          setIsInitializing(false);
        }
      }
    };

    // Check if Google Identity Services is already loaded
    // Must use typeof check to avoid ReferenceError when google is not defined
    if (typeof google !== "undefined" && google?.accounts?.id) {
      initializeGoogleSignIn();
    } else {
      // Wait for Google Identity Services to load
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds total (50 * 100ms)

      googleLoadInterval = setInterval(() => {
        attempts++;
        if (cancelled) {
          clearGoogleTimers();
          return;
        }

        // Must use typeof check to avoid ReferenceError when google is not defined
        if (typeof google !== "undefined" && google?.accounts?.id) {
          clearGoogleTimers();
          initializeGoogleSignIn();
        } else if (attempts >= maxAttempts) {
          clearGoogleTimers();
          console.error(
            "Google Identity Services script did not load after 5 seconds",
          );
          if (!cancelled) {
            setError(
              "Google Sign-In failed to load. Please check:\n1. Your internet connection\n2. That the Google script is loading\n3. Browser console for errors\n\nTry refreshing the page.",
            );
            setIsInitializing(false);
          }
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      clearGoogleTimers();
    };
  }, [
    GOOGLE_CLIENT_ID,
    handleCredentialResponse,
    isCheckingAuth,
    isFromBroadcaster,
    authToken,
    userError,
    broadcasterCallbackSent,
  ]);

  // Show broadcaster callback success screen
  if (broadcasterCallbackSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <img
                src="/Logo White Transparent.png"
                alt="FlowLabs Logo"
                className="h-20 w-auto"
              />
            </div>
          </div>

          <Card className="p-8 border-border shadow-xl">
            <div className="space-y-6 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold">Connected!</h2>
                <p className="text-muted-foreground">
                  You're now connected to Flow Broadcaster. You can close this
                  tab and return to the app.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Show "Return to Broadcaster" screen if already authenticated and from broadcaster
  if (isFromBroadcaster && authToken && !isCheckingAuth && !userError) {
    const user = userData?.me;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <img
                src="/Logo White Transparent.png"
                alt="FlowLabs Logo"
                className="h-20 w-auto"
              />
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-bold">Welcome back!</h1>
              {user?.email && (
                <p className="text-muted-foreground">
                  Signed in as {user.email}
                </p>
              )}
            </div>
          </div>

          <Card className="p-8 border-border shadow-xl">
            <div className="space-y-6">
              <p className="text-center text-muted-foreground">
                Click below to connect Flow Broadcaster to your account.
              </p>

              <Button
                onClick={() => {
                  const refreshToken = localStorage.getItem("refreshToken");
                  sendTokenToBroadcaster(authToken, refreshToken || undefined);
                }}
                className="w-full h-14 text-lg font-semibold bg-primary hover:bg-primary/90 notranslate"
                size="lg"
                translate="no"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                <AuthButtonLabel>Return to Broadcaster</AuthButtonLabel>
              </Button>

              <div className="text-center">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/")}
                  className="text-muted-foreground notranslate"
                  translate="no"
                >
                  <AuthButtonLabel>Go to Dashboard instead</AuthButtonLabel>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Logo and Header */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <img
              src="/Logo White Transparent.png"
              alt="FlowLabs Logo"
              className="h-20 w-auto"
            />
          </div>
          <div className="space-y-2">
            <h1 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">
              {isFromBroadcaster
                ? "Connect Broadcaster"
                : "Welcome to FlowLabs"}
            </h1>
            <p className="text-muted-foreground text-lg">
              {isFromBroadcaster
                ? "Sign in to connect your Flow account to the Broadcaster app"
                : "Sign in to access your streaming dashboard"}
            </p>
          </div>
        </div>

        {/* Login Card */}
        <Card className="p-8 border-border shadow-xl">
          {showForgotPassword ? (
            <div className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {resetSuccess ? (
                <div className="space-y-4 text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                    <Check className="w-7 h-7 text-green-500" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-semibold">Password reset!</p>
                    <p className="text-sm text-muted-foreground">
                      You can now sign in with your new password.
                    </p>
                  </div>
                  <Button
                    className="w-full h-12"
                    onClick={handleBackFromForgotPassword}
                  >
                    <AuthButtonLabel>Back to Sign In</AuthButtonLabel>
                  </Button>
                </div>
              ) : forgotStep === "email" ? (
                <form onSubmit={handleSendResetCode} className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-xl font-semibold">Reset your password</p>
                    <p className="text-sm text-muted-foreground">
                      Enter your email and we'll send you a reset code.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="forgot-email"
                      className="flex items-center gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      Email
                    </Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      disabled={sendCodeLoading}
                      className="h-12"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 text-base font-semibold notranslate"
                    disabled={sendCodeLoading || !forgotEmail}
                    translate="no"
                  >
                    {sendCodeLoading ? (
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    ) : null}
                    <AuthButtonLabel>
                      {sendCodeLoading ? "Sending..." : "Send Reset Code"}
                    </AuthButtonLabel>
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full notranslate"
                    onClick={handleBackFromForgotPassword}
                    translate="no"
                  >
                    <AuthButtonLabel>Back to Sign In</AuthButtonLabel>
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-xl font-semibold">Enter reset code</p>
                    <p className="text-sm text-muted-foreground">
                      We sent a 4-digit code to {forgotEmail}.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reset-code">Verification code</Label>
                    <Input
                      id="reset-code"
                      type="text"
                      placeholder="0000"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      disabled={resetLoading}
                      className="h-12 tracking-widest text-center text-xl"
                      maxLength={4}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="new-password"
                      className="flex items-center gap-2"
                    >
                      <Lock className="h-4 w-4" />
                      New password
                    </Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="New password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={resetLoading}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="confirm-password"
                      className="flex items-center gap-2"
                    >
                      <Lock className="h-4 w-4" />
                      Confirm password
                    </Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={resetLoading}
                      className="h-12"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 text-base font-semibold notranslate"
                    disabled={
                      resetLoading ||
                      !resetCode ||
                      !newPassword ||
                      !confirmPassword
                    }
                    translate="no"
                  >
                    {resetLoading ? (
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    ) : null}
                    <AuthButtonLabel>
                      {resetLoading ? "Resetting..." : "Reset Password"}
                    </AuthButtonLabel>
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full notranslate"
                    onClick={() => {
                      setForgotStep("email");
                      setResetCode("");
                      setNewPassword("");
                      setConfirmPassword("");
                      setError("");
                    }}
                    translate="no"
                  >
                    <AuthButtonLabel>Back</AuthButtonLabel>
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="whitespace-pre-line">
                    {error}
                    {error.toLowerCase().includes("sign up") && (
                      <span className="block mt-2">
                        <a
                          href="/signup"
                          className="underline font-medium hover:text-destructive-foreground/80"
                        >
                          Click here to create an account
                        </a>
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                {/* Google Sign-In Button with Custom Branding */}
                <div className="w-full relative">
                  {/* Google's button container - hidden but functional */}
                  <div
                    ref={buttonRef}
                    className="absolute inset-0 opacity-0 pointer-events-auto z-10"
                    id="google-signin-button-container"
                    style={{ minHeight: "56px" }}
                  />

                  {/* Custom branded overlay */}
                  <div className="relative pointer-events-none">
                    <Button
                      disabled={isInitializing || isLoading || isCheckingAuth}
                      className="w-full h-14 text-lg font-semibold bg-primary hover:bg-primary/90 text-primary-foreground transition-all pointer-events-none notranslate"
                      size="lg"
                      translate="no"
                    >
                      {isCheckingAuth ? (
                        <>
                          <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                          <AuthButtonLabel>
                            Checking authentication...
                          </AuthButtonLabel>
                        </>
                      ) : isInitializing ? (
                        <>
                          <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                          <AuthButtonLabel>Loading...</AuthButtonLabel>
                        </>
                      ) : isLoading ? (
                        <>
                          <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                          <AuthButtonLabel>Signing in...</AuthButtonLabel>
                        </>
                      ) : (
                        <>
                          <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                              fill="currentColor"
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                              fill="currentColor"
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                              fill="currentColor"
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                          </svg>
                          <AuthButtonLabel>Sign in with Google</AuthButtonLabel>
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {!showEmailForm ? (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">
                          Or
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      className="w-full h-12 notranslate"
                      onClick={() => setShowEmailForm(true)}
                      translate="no"
                    >
                      <Mail className="mr-2 h-5 w-5" />
                      <AuthButtonLabel>Sign in with Email</AuthButtonLabel>
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">
                          Sign in with email
                        </span>
                      </div>
                    </div>

                    <form onSubmit={handleEmailSignIn} className="space-y-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="email"
                          className="flex items-center gap-2"
                        >
                          <Mail className="h-4 w-4" />
                          Email
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={emailLoading}
                          className="h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="password"
                          className="flex items-center gap-2"
                        >
                          <Lock className="h-4 w-4" />
                          Password
                        </Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="Your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={emailLoading}
                          className="h-12"
                        />
                        <div className="text-right">
                          <button
                            type="button"
                            className="text-sm text-primary hover:underline notranslate"
                            onClick={() => {
                              setShowForgotPassword(true);
                              setForgotEmail(email);
                              setError("");
                            }}
                            translate="no"
                          >
                            <AuthButtonLabel>Forgot password?</AuthButtonLabel>
                          </button>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        className="w-full h-12 text-base font-semibold notranslate"
                        disabled={emailLoading || !email || !password}
                        translate="no"
                      >
                        {emailLoading ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            <AuthButtonLabel>Signing in...</AuthButtonLabel>
                          </>
                        ) : (
                          <AuthButtonLabel>Sign In</AuthButtonLabel>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="w-full notranslate"
                        onClick={() => setShowEmailForm(false)}
                        translate="no"
                      >
                        <AuthButtonLabel>
                          Back to Google Sign In
                        </AuthButtonLabel>
                      </Button>
                    </form>
                  </>
                )}

                <div className="text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Don't have an account?{" "}
                    <Link
                      to="/signup"
                      className="text-primary hover:underline font-medium"
                    >
                      Sign up
                    </Link>
                  </p>
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                    <span>By continuing, you agree to FlowLabs'</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 text-xs">
                    <a
                      href="https://flowlabs.live/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Terms of Service
                    </a>
                    <span className="text-muted-foreground">and</span>
                    <a
                      href="https://flowlabs.live/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Privacy Policy
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Login;
